import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api/client";
import { tenantPath, useTenantSlug } from "../lib/tenant";
import type { PairingSlot, SampleHome } from "../lib/api/types";
import { useVisualizer } from "../lib/state/visualizerStore";
import { AppShell, card } from "../components/AppShell";
import { UploadIcon, PhoneIcon, PinIcon, WarningIcon } from "../components/icons";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { Button } from "../components/Button";
import { Spinner } from "../components/Spinner";

// A warning surfaced back on the select screen (e.g. an address we couldn't turn into a home photo).
type SelectError = { title: string; message: string; address?: string };

// Built-in fallback homes (served from shared storage), shown only when the dealer/global sample library
// is empty so the "try a sample" row is never blank. Clicking one runs detection exactly like an upload.
const FALLBACK_SAMPLES: SampleHome[] = [
  { url: "/storage/template/t1.jpg", label: "Sample home 1" },
  { url: "/storage/template/t2.jpg", label: "Sample home 2" },
  { url: "/storage/template/t3.jpg", label: "Sample home 3" },
];

// Keep the starter row compact — the dealer's full library lives in the proposal builder; here a handful
// of quick-try homes is enough.
const MAX_SAMPLES = 6;

export function SelectPage() {
  const tenantSlug = useTenantSlug();
  const navigate = useNavigate();
  const initFromExtraction = useVisualizer((s) => s.initFromExtraction);
  const setSourceAddress = useVisualizer((s) => s.setSourceAddress);
  const [pairing, setPairing] = useState<PairingSlot | null>(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [error, setError] = useState<SelectError | null>(null);
  const [address, setAddress] = useState("");
  const [dragOver, setDragOver] = useState(false);
  // The dealer's sample library (global starters + their own uploads); null = still loading. Falls back to
  // the built-in homes below when the tenant has none, so the row is never blank.
  const [samples, setSamples] = useState<SampleHome[] | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    api.createPairing(tenantSlug).then(setPairing).catch(() => {});
    api.getSamples(tenantSlug).then(setSamples).catch(() => setSamples([]));
    return () => { cancelled.current = true; };
  }, [tenantSlug]);

  // Dealer/global samples when present, else the built-in trio; capped so the starter row stays compact.
  const sampleHomes = (samples && samples.length > 0 ? samples : FALLBACK_SAMPLES).slice(0, MAX_SAMPLES);

  useEffect(() => {
    if (!pairing?.pairingId || busy) return;
    const timer = setInterval(async () => {
      try {
        const slot = await api.getPairing(tenantSlug, pairing.pairingId);
        if (slot.status === "uploaded" && slot.photoUrl) {
          clearInterval(timer);
          void runExtraction(slot.photoUrl);
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairing?.pairingId, busy, tenantSlug]);

  function onAddressChange(value: string) {
    setAddress(value);
    if (error) setError(null); // editing the address clears a prior "not found" warning
  }

  async function handleFile(file: File) {
    setBusy(true);
    setError(null);
    try {
      const ticket = await api.createUploadTicket(tenantSlug, file);
      await api.uploadToTicket(ticket, file);
      await runExtraction(ticket.publicUrl);
    } catch {
      setError({ title: "Upload failed", message: "We couldn't upload that image. Please try again, or use your phone." });
      setBusy(false);
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) void handleFile(file);
    else if (file) setError({ title: "Unsupported file", message: "Please drop a JPG or PNG image of your home." });
  }

  async function resolveByAddress() {
    const value = address.trim();
    if (!value) return;
    setBusy(true);
    setError(null);
    setStage("locating");
    try {
      const { photoUrl, formattedAddress } = await api.resolveAddressPhoto(tenantSlug, value);
      // Carry the address into the session so the quote form arrives prefilled — the customer
      // already told us where they live; making them re-type it is a conversion tax. Prefer the
      // geocoder's canonical formatting over what they typed.
      await runExtraction(photoUrl, formattedAddress || value);
    } catch {
      // The address didn't resolve to a home photo (bad address, no Street View, or no house found).
      // Drop back to the select screen with a clear warning so they can upload, scan, or retry.
      setError({
        title: "We couldn't find your home",
        message: "We couldn't find a street-level photo of the home at this address. Please upload a photo of your home, scan it with your phone, or try a different address.",
        address: value,
      });
      setBusy(false);
    }
  }

  async function runExtraction(photoUrl: string, sourceAddress?: string) {
    setBusy(true);
    setError(null);
    setStage("queued");
    try {
      const { jobId, sessionId } = await api.startExtract(tenantSlug, photoUrl);
      for (let i = 0; i < 90; i++) {
        if (cancelled.current) return;
        await new Promise((r) => setTimeout(r, 2000));
        const status = await api.getExtract(tenantSlug, jobId);
        setStage(status.stage);
        if (status.status === "completed") {
          initFromExtraction(photoUrl, sessionId, status.objects);
          // After init (which resets the document): remember where this photo came from so the
          // quote form can prefill the address for the resolve-by-address flow.
          if (sourceAddress) setSourceAddress(sourceAddress);
          navigate(tenantPath(tenantSlug, "visualize"));
          return;
        }
        if (status.status === "failed" || status.status === "expired") {
          setError({ title: "No garage door found", message: status.error ?? "We couldn't find a garage door in that photo. Try another photo, or scan with your phone." });
          setBusy(false);
          return;
        }
      }
      setError({ title: "This is taking longer than expected", message: "Detection timed out. Please try again." });
      setBusy(false);
    } catch {
      setError({ title: "Something went wrong", message: "Please try again in a moment." });
      setBusy(false);
    }
  }

  return (
    <AppShell step="select">
      <div style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 24px" }}>
        <h1 style={{ fontSize: "clamp(22px, 5vw, 28px)", margin: "0 0 6px", lineHeight: 1.2 }}>Visualize a new garage door</h1>
        <p style={{ color: "var(--muted)", margin: 0, fontSize: "clamp(14px, 3.5vw, 15px)" }}>
          See a new door on your home in seconds — choose how to start below.
        </p>
      </div>

      {busy ? (
        <AnalyzingCard stage={stage} />
      ) : (
        <>
          {error && (
            <div role="alert" data-testid="select-warning" style={{ ...card, display: "flex", gap: 12, alignItems: "flex-start", borderColor: "var(--danger)", background: "rgba(220,38,38,0.06)", marginBottom: 16 }}>
              <span style={{ color: "var(--danger)", flexShrink: 0, marginTop: 2 }} aria-hidden><WarningIcon size={22} /></span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "var(--text)" }}>{error.title}</div>
                <div style={{ color: "var(--muted)", fontSize: 14, marginTop: 4 }}>{error.message}</div>
                {error.address && (
                  <div data-testid="select-warning-address" style={{ display: "inline-block", marginTop: 8, padding: "4px 10px", borderRadius: "var(--radius-pill)", background: "var(--surface)", border: "1px solid var(--border)", fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                    {error.address}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sample homes — a no-photo fallback, above the three start options. Capped to the same centred
              900px column as the option-card grid below (.rg-select-grid) so the two rows line up, and
              auto-FIT (not auto-fill) so 1..MAX_SAMPLES tiles fill the row in equal widths — hence equal
              heights — instead of huddling left in tiny auto-fill cells. */}
          <div style={{ maxWidth: 900, margin: "0 auto 18px" }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: "var(--text)" }}>
              No photo handy? Try it on a sample home
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
              {sampleHomes.map((s) => (
                <button
                  key={s.url}
                  type="button"
                  className="rg-option"
                  data-testid="sample-home"
                  onClick={() => void runExtraction(s.url)}
                  style={{ position: "relative", padding: 0, overflow: "hidden", cursor: "pointer", border: "1px solid var(--border)", borderRadius: "var(--radius)", background: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
                >
                  <img src={s.url} alt={s.label ?? "Sample home"} style={{ width: "100%", aspectRatio: "16 / 10", objectFit: "cover", display: "block" }} />
                  <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "10px 10px 7px", background: "linear-gradient(transparent, rgba(15,23,42,0.6))", color: "#fff", fontSize: 12, fontWeight: 600, textAlign: "left" }}>
                    Try this home
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="rg-select-grid">
            {/* Upload — whole card is a click-or-drop target. */}
            <label
              className="rg-option"
              style={{ ...optionCard, cursor: "pointer", borderColor: dragOver ? "var(--brand)" : "var(--border)", background: dragOver ? "var(--brand-tint)" : "var(--surface)" }}
              onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <span style={iconCircle}><UploadIcon size={24} /></span>
              <span style={cardTitle}>Upload a photo</span>
              <span style={cardDesc}>A clear, straight-on photo of your home</span>
              <span style={cardBody}>
                <span style={{ flex: 1, alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 14, borderRadius: "var(--radius)", border: `1.5px dashed ${dragOver ? "var(--brand)" : "var(--border-strong)"}`, color: dragOver ? "var(--brand-active)" : "var(--muted)", fontSize: 13 }}>
                  {dragOver ? "Drop to upload" : "Drag & drop your photo here"}
                </span>
              </span>
              <input type="file" accept="image/*" onChange={onInputChange} style={{ display: "none" }} />
              <span className="rg-btn rg-btn--primary rg-btn--block">Browse files</span>
            </label>

            {/* Phone — QR handoff to a second device. */}
            <div style={optionCard}>
              <span style={iconCircle}><PhoneIcon size={24} /></span>
              <span style={cardTitle}>Use your phone</span>
              <span style={cardDesc}>Snap a photo on the go — no app needed</span>
              <span style={cardBody}>
                {pairing?.qrUrl ? (
                  <span style={{ padding: 8, background: "#fff", borderRadius: "var(--radius)", border: "1px solid var(--border)", display: "inline-flex", boxShadow: "var(--shadow-sm)" }}>
                    <QRCodeSVG value={pairing.qrUrl} size={132} aria-label="QR code to open the camera on your phone" />
                  </span>
                ) : (
                  <Spinner size={28} />
                )}
              </span>
              <span style={{ color: "var(--muted-2)", fontSize: 12, minHeight: 18 }}>Scan with your phone camera</span>
            </div>

            {/* Address — resolve a Street View photo. */}
            <div data-testid="resolve-address-card" style={optionCard}>
              <span style={iconCircle}><PinIcon size={24} /></span>
              <span style={cardTitle}>Use your address</span>
              <span style={cardDesc}>We'll find a photo of your home for you</span>
              <span style={cardBody}>
                <span style={{ width: "100%" }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void resolveByAddress(); } }}>
                  <AddressAutocomplete value={address} onChange={onAddressChange} testId="address-input" placeholder="Enter your home address" />
                </span>
              </span>
              <Button variant="primary" block disabled={!address.trim()} onClick={() => void resolveByAddress()} data-testid="resolve-address-submit">
                Find my home
              </Button>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}

// Shared option-card styling so the three choices are visually identical: equal height (grid stretch +
// height:100%), the same icon → title → description rhythm, a flexible centred body, and a bottom action.
const optionCard: React.CSSProperties = {
  ...card,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 10,
  padding: 24,
  height: "100%",
};
const iconCircle: React.CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  background: "var(--brand-tint)",
  color: "var(--brand)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
const cardTitle: React.CSSProperties = { fontSize: 16, fontWeight: 600, color: "var(--text)" };
const cardDesc: React.CSSProperties = { color: "var(--muted)", fontSize: 13, minHeight: 34, display: "flex", alignItems: "center" };
const cardBody: React.CSSProperties = { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", gap: 10, padding: "6px 0 10px" };

// Friendly, customer-facing copy for each backend pipeline stage, plus a progress fraction so the bar
// advances as the job moves through detect → segment → fit (and the address "locating" pre-step).
const STAGE_LABELS: Record<string, string> = {
  locating: "Finding your home…",
  queued: "Getting things ready…",
  detecting: "Looking for your garage door…",
  segmenting: "Tracing the door edges…",
  fitting: "Measuring the opening…",
  completed: "Finishing up…",
};
const STAGE_PCT: Record<string, number> = { locating: 15, queued: 30, detecting: 55, segmenting: 78, fitting: 92, completed: 100 };
const TIPS = [
  "Our AI is studying your home's photo.",
  "Pinpointing the garage door opening.",
  "Lining up the panels and edges.",
  "Hang tight — this only takes a moment.",
];

// Interactive "analyzing" screen: a rotating spinner, the current step, an advancing progress bar, and a
// rotating tip so it feels alive while the (multi-second) AI pipeline runs.
function AnalyzingCard({ stage }: { stage: string | null }) {
  const [tip, setTip] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTip((t) => (t + 1) % TIPS.length), 2500);
    return () => clearInterval(id);
  }, []);
  const label = (stage && STAGE_LABELS[stage]) || "Analyzing your photo…";
  const pct = (stage && STAGE_PCT[stage]) || 25;
  return (
    <div data-testid="analyzing-card" style={{ ...card, textAlign: "center", padding: "32px 24px", maxWidth: 460, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <Spinner size={44} thickness={4} />
      </div>
      <div data-testid="analyzing-stage" style={{ fontSize: 18, fontWeight: 700 }}>{label}</div>
      <div style={{ height: 6, borderRadius: 999, background: "var(--bg)", overflow: "hidden", margin: "16px auto 0", maxWidth: 320 }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "var(--brand)", borderRadius: 999, transition: "width 600ms ease" }} />
      </div>
      <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 12, minHeight: 18 }}>{TIPS[tip]}</div>
    </div>
  );
}
